import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Slider,
  Text,
  mergeClasses,
} from "@fluentui/react-components";
import { DismissRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { CustomToggle } from "components/customToggle/CustomToggle";
import React, { useEffect, useState } from "react";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import {
  useSharedDialogFooterStyles,
  useSharedDialogContentStyles,
} from "theme/sharedStyles";
import { useStyles } from "./createDistributionDialog.styles";
import { ICreateDistributionDialogProps } from "./createDistributionDialog.types";
import { ConstantValues } from "common/constants";

export const CreateDistributionDialog = (
  props: ICreateDistributionDialogProps,
): JSX.Element => {
  const {
    isOpen,
    onDismiss,
    onConfirm,
    speechEnabled,
    onSpeechEnabledChange,
    label,
    onLabelChange,
    maxConnections,
    onMaxConnectionsChange,
  } = props;
  const styles = useStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const [labelError, setLabelError] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  useEffect(() => {
    if (!isOpen) setLabelError(ConstantValues.EMPTY_STRING);
  }, [isOpen]);

  const handleLabelChange = (value: string) => {
    if (labelError && value.trim()) setLabelError(ConstantValues.EMPTY_STRING);
    onLabelChange(value);
  };

  const handleConfirm = () => {
    if (!label.trim()) {
      setLabelError("Name is required.");
      return;
    }
    if (label.trim().length > 255) {
      setLabelError("Name must not exceed 255 characters.");
      return;
    }
    onConfirm();
  };

  const isNameMissing = !label.trim();

  return (
    <Dialog open={isOpen} onOpenChange={onDismiss}>
      <DialogSurface
        className={mergeClasses(
          footerStyles.dialogSurface,
          contentStyles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => onDismiss()}
              />
            }
          >
            Create Distribution
          </DialogTitle>
          <DialogContent>
            <Field
              label="Name"
              required
              className={styles.labelField}
              validationState={labelError ? "error" : undefined}
              validationMessage={labelError || undefined}
            >
              <Input
                value={label}
                onChange={(_, data) => handleLabelChange(data.value)}
                placeholder="e.g. Marketing Team, Q1 2025..."
              />
            </Field>
            <CustomToggle
              checked={speechEnabled}
              onChange={onSpeechEnabledChange}
              label="Enable Speech Functionality"
              hint="Enable an interviewee to use Speech functionality (default), or only text entry."
            />
            {speechEnabled ? (
              <Text size={200} className={styles.helpText}>
                Interviewees will have the option to use Speech to Speech
                functionality when taking the survey.
              </Text>
            ) : (
              <Text size={200} className={styles.helpText}>
                Interviewees will only be able to enter text via keyboard and
                will not see the Speech/Audio toggle.
              </Text>
            )}
            <div className={styles.divider} />
            <Field
              label="Anticipated Responses"
              required
              className={styles.labelField}
            >
              <div className={styles.sliderRow}>
                <Input
                  type="number"
                  value={String(maxConnections)}
                  className={styles.sliderInput}
                  onChange={(_, data) => {
                    const parsed = parseInt(data.value, 10);
                    if (!isNaN(parsed)) {
                      onMaxConnectionsChange(
                        Math.max(1, Math.min(5000, parsed)),
                      );
                    }
                  }}
                  onBlur={() => {
                    if (maxConnections < 1) onMaxConnectionsChange(1);
                    if (maxConnections > 5000) onMaxConnectionsChange(5000);
                  }}
                />
                <Slider
                  min={1}
                  max={5000}
                  value={maxConnections}
                  className={styles.slider}
                  onChange={(_e, data) => onMaxConnectionsChange(data.value)}
                />
                <Text size={200} className={styles.sliderMax}>
                  max 5,000
                </Text>
              </div>
            </Field>
            <Text size={200} className={styles.helpText}>
              Maximum number of expected audience.
            </Text>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            onClick={handleConfirm}
            tooltip="Create"
            dimmed={isNameMissing}
            ariaLabel={isNameMissing ? "Create (enter a name first)" : "Create"}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
